import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { Accept } from 'react-dropzone';
import { AcceptedFileTypeKeys } from '../../../types/enums';
import { ACCEPTED_FILE_TYPES } from '../../../types/constants';
import {
  useFileFieldController,
  UseFileFieldControllerProps,
} from '../../../hooks/form/useFileFieldController';
import { getFileNameFromPath } from '../../../helpers/shared/getFileNameFromPath';
import { MuiStack } from '../../../../../modules/material';
import ErrorMessage from '../shared/ErrorMessage';
import FormFieldWrapper from '../../library/FormFieldWrapper';
import FileUpload from '../../library/FileUpload';

export interface FileFieldProps<TFieldValues>
  extends UseFileFieldControllerProps<TFieldValues> {
  limit?: number;
  fileTypes?: Array<AcceptedFileTypeKeys>;
  label?: ReactNode;
  helperText?: ReactNode;
  errorMessage?: ReactNode;
}

export default function FileField<TFieldValues extends FieldValues>(
  props: FileFieldProps<TFieldValues>,
) {
  const {
    label,
    helperText,
    limit,
    name,
    shouldUnregister,
    disabled,
    defaultValue,
    rules,
    fileTypes = [],
  } = props;

  const {
    field,
    required,
    formState: { errors },
    fieldState: { invalid: error },
    files,
    handleFileSelect,
    handleFileRemove,
  } = useFileFieldController<TFieldValues>({
    name,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
  });

  const showFileUpload = !limit || files.length < limit;

  const fileUploadAccept = fileTypes.reduce((accept, key) => {
    const acceptedFileType = ACCEPTED_FILE_TYPES[key];

    if (acceptedFileType) {
      return { ...accept, ...acceptedFileType };
    }

    return accept;
  }, {} as Accept);

  return (
    <FormFieldWrapper
      label={label}
      required={required}
      helperText={helperText}
      error={error}
      errorMessage={<ErrorMessage name={field.name} errors={errors} />}
      fullWidth
      data-testid="file-field"
    >
      <MuiStack spacing="8px">
        {files.map((file, index) => {
          const fileName = file.name || getFileNameFromPath(file.path);

          return (
            <MuiStack key={index}>
              <FileUpload
                uploaded
                progress={100}
                fileName={fileName}
                onRemove={() => {
                  handleFileRemove(index);
                }}
              />
            </MuiStack>
          );
        })}

        {showFileUpload && (
          <MuiStack>
            <input type="hidden" {...field} />
            <FileUpload
              accept={fileUploadAccept}
              onDropAccepted={(acceptedFiles) => {
                handleFileSelect(acceptedFiles[0]);
              }}
            />
          </MuiStack>
        )}
      </MuiStack>
    </FormFieldWrapper>
  );
}
